<template>
  <CCard class="contract-intro-price">
    <CCardHeader>
      <div class="section-heading">
        <div>
          <strong>初期特別価格</strong>
          <p>
            契約開始から一定期間、基本料と1人あたりの料金を特別価格に設定できます。
            0円を入力すると無料になります。
          </p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <!--
        カードの頭に出すのは、読み込みの失敗だけ（2026-09-26）。
        保存・解除の結果と、送る前の断りは、ボタンのすぐ下に出す。
      -->
      <CAlert v-if="loadError" color="danger">{{ loadError }}</CAlert>

      <div v-if="loading" class="contract-intro-price__loading">読み込んでいます…</div>

      <template v-else>
        <p v-if="!canEdit" class="text-muted">
          初期特別価格の変更には「契約情報を更新」権限が必要です。
        </p>
        <CAlert v-if="!linked" color="warning">
          この契約企業は、まだ契約が登録されていません。
          先に契約を保存してから設定してください。
        </CAlert>

        <div class="intro-grid">
          <div>
            <label for="intro-months">期間（か月）</label>
            <CInput
              id="intro-months"
              :disabled="!canEdit"
              v-model="form.intro_months"
              type="number"
              min="1"
              max="120"
              step="1"
              description="契約開始月を1か月目として数えます（月途中の開始でも1か月目とみなします）。"
            />
          </div>
          <div>
            <label for="intro-base">基本料（円／月）</label>
            <CInput
              id="intro-base"
              :disabled="!canEdit"
              v-model="form.intro_base_price"
              type="number"
              min="0"
              step="1"
              description="空欄の場合は通常の料金が適用されます。0円を入力すると無料になります。"
            />
          </div>
          <div>
            <label for="intro-per-user">1人あたり料金（円／月）</label>
            <CInput
              id="intro-per-user"
              :disabled="!canEdit"
              v-model="form.intro_price_per_user"
              type="number"
              min="0"
              step="1"
              description="空欄の場合は通常の料金が適用されます。0円を入力すると無料になります。"
            />
          </div>
          <div>
            <label for="intro-remarks">備考</label>
            <CInput id="intro-remarks" v-model.trim="form.remarks" maxlength="200" :disabled="!canEdit"/>
          </div>
        </div>

        <!--
          **いつからいつまで効くかを、保存する前に見せる。**
          「3か月」と入れても、月の途中に始まった契約では何月までかが
          人によって違って読める。数え方を画面で確定させる。
        -->
        <p class="intro-period" :class="{ 'intro-period--warning': periodWarning }">
          {{ periodText }}
        </p>
        <p v-if="freeWhole" class="intro-period intro-period--note">
          基本料・1人あたり料金ともに0円のため、この期間の請求書は発行されません。
        </p>

        <div v-if="canEdit" class="contract-intro-price__actions">
          <CButton
            v-if="hasCurrent"
            type="button"
            color="secondary"
            variant="outline"
            :disabled="saving || !linked"
            @click="stop"
          >初期特別価格を解除する</CButton>
          <CButton
            type="button"
            color="primary"
            :disabled="saving || !linked"
            @click="save"
          >{{ saving ? '保存しています…' : '保存する' }}</CButton>
        </div>
        <ActionResultMessage :text="successMessage" type="success"/>
        <ActionResultMessage :text="errorMessage" type="danger"/>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import api from '@/services/accessManagement'
import { safeResponseMessage, safeErrorDigest } from '@/utils/safeResponseMessage'

/**
 * 契約ごとの初期特別価格（0100）。
 *
 * <h2>何を決めるか</h2>
 *
 * 「最初の N か月は 基本料 ○円・1人あたり ○円」を<b>金額で</b>決める。
 * 0円なら無料。空欄のほうは通常の料金のまま。数え始めは契約開始の月
 * （月の途中に始まっても、その月が1か月目）。利用者の決め（2026-09-22）。
 *
 * <h2>どこから見えるか</h2>
 *
 * 運営メニュー → 契約企業管理 → 会社を選ぶ → 「契約に関わる会社」の下。
 *
 * <h2>確定済みの請求は動かない</h2>
 *
 * 月次確定は、確定した時点の基本料と単価を請求ごとに写して持つ。
 * ここを変えて効くのは、まだ確定していない月だけ。
 */
export default {
  name: 'ContractIntroPrice',

  components: { ActionResultMessage },

  props: {
    accountId: { type: Number, required: true },
    /** 契約を変えられるか（CONTRACT_UPDATE）。false なら読むだけ。 */
    canEdit: { type: Boolean, default: false }
  },

  data () {
    return {
      form: this.空の入力(),
      contractStartDate: '',
      hasCurrent: false,
      linked: false,
      // **いま画面に出ている値は、どの会社のものか。**（監査 F2）
      loadedAccountId: null,
      loading: true,
      saving: false,
      /* 読み込みの失敗（カードの頭に出す）。 */
      loadError: '',
      /* 保存・解除の結果と送る前の断り（ボタンのすぐ下に出す）。 */
      errorMessage: '',
      successMessage: ''
    }
  },

  watch: {
    accountId: {
      immediate: true,
      handler () {
        // **前の会社の保存は、この画面ではもう扱わない**（監査 F2 の残り）。
        // 終わっても入力を読み直さず、通知も出さない。押せない状態も持ち越さない。
        this.$_saveOp = (this.$_saveOp || 0) + 1
        this.saving = false
        this.load()
      }
    }
  },

  computed: {
    months () {
      const text = String(this.form.intro_months === null ? '' : this.form.intro_months).trim()
      if (!/^\d+$/.test(text)) return null
      const value = Number(text)
      return value >= 1 && value <= 120 ? value : null
    },

    /** いつからいつまでが特別価格か。**画面で数え方を確定させる。** */
    periodText () {
      if (!this.contractStartDate) {
        return '契約開始日が未入力です。開始日が設定されるまで、初期特別価格は適用されません。'
      }
      if (this.months === null) {
        return '期間を入れると、特別価格になる月をここに表示します。'
      }
      const start = this.月初(this.contractStartDate)
      if (!start) {
        return '契約開始日を読み取れませんでした。'
      }
      const last = this.月を足す(start, this.months - 1)
      const next = this.月を足す(start, this.months)
      return this.年月(start) + '〜' + this.年月(last) + 'は特別価格です。'
        + this.年月(next) + 'から通常の料金に戻ります。'
    },

    periodWarning () {
      return !this.contractStartDate
    },

    /** 基本料も1人あたりも0円か（その期間は請求書を作らない）。 */
    freeWhole () {
      return this.months !== null
        && this.isZero(this.form.intro_base_price)
        && this.isZero(this.form.intro_price_per_user)
    }
  },

  methods: {
    空の入力 () {
      return { intro_months: '', intro_base_price: '', intro_price_per_user: '', remarks: '' }
    },

    isZero (value) {
      const text = String(value === null || value === undefined ? '' : value).trim()
      return text !== '' && Number(text) === 0
    },

    月初 (text) {
      const found = /^(\d{4})-(\d{2})/.exec(String(text))
      return found ? { year: Number(found[1]), month: Number(found[2]) } : null
    },

    月を足す (start, count) {
      const index = start.year * 12 + (start.month - 1) + count
      return { year: Math.floor(index / 12), month: (index % 12) + 1 }
    },

    年月 (value) {
      return value.year + '年' + value.month + '月'
    },

    文字に (value) {
      return value === null || value === undefined ? '' : String(value)
    },

    /**
     * **会社を切り替えたら、前の会社の返事は捨てる（監査 F2）。**
     *
     * A を開いてすぐ B へ移ると、A の返事が後から届いて B の画面に
     * A の値が入り、そのまま保存すると **A の条件が B に書かれる。**
     * 何番目の読み込みか・どの会社宛てかを覚えておき、合わない返事は使わない。
     */
    async load () {
      // 画面に出さない数なので data に置かない（描き直しを起こさない）。
      const seq = this.$_introSeq = (this.$_introSeq || 0) + 1
      const target = this.accountId
      this.loading = true
      this.loadedAccountId = null
      this.loadError = ''
      this.errorMessage = ''
      this.successMessage = ''
      this.form = this.空の入力()
      const stale = () => seq !== this.$_introSeq || target !== this.accountId
      try {
        const current = await api.getContractIntroPrice(target)
        if (stale()) return
        this.linked = !!current
        this.contractStartDate = current && current.contract_start_date
          ? String(current.contract_start_date).slice(0, 10) : ''
        this.hasCurrent = !!(current && current.intro_months)
        if (this.hasCurrent) {
          this.form = {
            intro_months: this.文字に(current.intro_months),
            intro_base_price: this.文字に(current.intro_base_price),
            intro_price_per_user: this.文字に(current.intro_price_per_user),
            remarks: current.remarks || ''
          }
        }
        this.loadedAccountId = target
      } catch (error) {
        if (stale()) return
        this.loadError = '初期特別価格を読み込めませんでした。'
        this.$emit('load-error', safeErrorDigest(error))
        this.linked = false
      } finally {
        if (!stale()) this.loading = false
      }
    },

    /** 保存してよいか。読み込み中・別の会社の値・権限なしでは押させない。 */
    canSend () {
      return this.canEdit && !this.saving && !this.loading
        && this.loadedAccountId !== null && this.loadedAccountId === this.accountId
    },

    /** 送る前に、サーバと同じ断り方で止める（文言をそろえる）。 */
    whatIsWrong () {
      if (this.months === null) {
        return '期間は1〜120か月で入力してください。'
      }
      const base = String(this.form.intro_base_price).trim()
      const perUser = String(this.form.intro_price_per_user).trim()
      if (base === '' && perUser === '') {
        return '基本料か1人あたり料金の、少なくとも片方を入力してください（無料にする場合は0円）。'
      }
      // **小数は送らない**（請求に入る金額は整数円だけ。back も断る）。1000.00 は整数として通す。
      const wrongAmount = value => value !== '' && !(Number(value) >= 0 && Number.isInteger(Number(value)))
      if (wrongAmount(base) || wrongAmount(perUser)) {
        return '料金は0円以上の整数（小数点なし）で入力してください。'
      }
      return ''
    },

    async save () {
      if (!this.canSend()) return
      const wrong = this.whatIsWrong()
      if (wrong) {
        this.errorMessage = wrong
        this.successMessage = ''
        return
      }
      const base = String(this.form.intro_base_price).trim()
      const perUser = String(this.form.intro_price_per_user).trim()
      await this.send({
        account_id: this.loadedAccountId,
        intro_months: this.months,
        intro_base_price: base === '' ? null : Number(base),
        intro_price_per_user: perUser === '' ? null : Number(perUser),
        remarks: this.form.remarks
      }, '初期特別価格を保存しました。')
    },

    /** 月数を空で送ると「やめる」。前の条件は消さずに閉じる。 */
    async stop () {
      if (!this.canSend()) return
      await this.send({
        account_id: this.loadedAccountId,
        intro_months: null,
        intro_base_price: null,
        intro_price_per_user: null,
        remarks: ''
      }, '初期特別価格を解除しました。次回確定分より通常料金が適用されます。')
    },

    /**
     * **保存を始めた会社を覚えておく（監査 F2 の残り）。**
     *
     * A 社の保存を待つあいだに B 社へ移ると、前は A の保存が終わった
     * ところで B を読み直して、B で入力中の値が消え、B の画面に
     * 「保存しました」が出た。終わったときに会社か操作が変わっていたら、
     * いまの画面には何もしない（成功でも失敗でも）。
     */
    async send (payload, done) {
      const target = this.loadedAccountId
      const op = this.$_saveOp = (this.$_saveOp || 0) + 1
      const moved = () => op !== this.$_saveOp || target !== this.accountId
      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await api.replaceContractIntroPrice(payload)
        // 最低月額のタブが「今月は初期特別価格の期間か」を読み直せるよう、親へ知らせる。
        this.$emit('saved', target)
        if (moved()) return
        // **保存できた値を読み直す。**送ったつもりの値ではなく、入っている値を見せる。
        await this.load()
        if (moved()) return
        this.successMessage = done
      } catch (error) {
        if (moved()) return
        this.errorMessage = safeResponseMessage(
          error, '初期特別価格を保存できませんでした。')
      } finally {
        if (!moved()) this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.contract-intro-price { min-width: 0; margin-bottom: 18px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; width: 100%; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }

.contract-intro-price__loading { color: #5c6873; }

.intro-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.75rem;
}

label { display: block; font-weight: 600; font-size: 0.8rem; }

.intro-period {
  margin: 0.75rem 0 0;
  color: #17384d;
  font-size: 0.85rem;
}

.intro-period--warning { color: #b54708; }
.intro-period--note { color: #6a7f8c; font-size: 0.8rem; }

.contract-intro-price__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}
</style>
